import { labelForKey, specialItems as specialCatalog } from '../data/inventory';
import { propertyFor, propertyImages } from '../data/propertyImages';
import type { FunnelData } from '../types/funnel';

const homeTypeLabel: Record<string, string> = { apartment: 'Apartment', house: 'House', townhome: 'Townhome', office: 'Office / special' };

function sizeText(d: FunnelData): string {
  if (d.homeType === 'office') return d.officeSize ? `${d.officeSize} office` : 'Office size not chosen';
  const b = d.bedrooms;
  if (!b) return 'Size not chosen';
  if (b === 'studio') return 'Studio';
  if (b === 'few') return 'Only a few items';
  if (b === '5') return '5+ bedrooms';
  return `${b} bedroom${b === '1' ? '' : 's'}`;
}

/**
 * Plain-English move summary for staff and CRM text fields (HighLevel “IM Inventory Summary” / “IM Home Type”).
 * Same selections the customer tapped — size, home type, example photo, every item variant with quantity, stops and services.
 */
export function buildMoveSummary(d: FunnelData): { headline: string; photo: string; items: string; special: string; stops: string; services: string; truck: string } {
  const prop = propertyFor(d.bedrooms, d.homeType);
  const items = Object.entries(d.inventory)
    .filter(([, q]) => q > 0)
    .map(([k, q]) => `${labelForKey(k, 'en')} ×${q}${d.itemNotes[k] ? ` (${d.itemNotes[k]})` : ''}`)
    .concat(d.customItems.map((c) => `${c.name} ×${c.qty} (custom)`));
  const special = d.specialItems.map((id) => specialCatalog.find((s) => s.id === id)?.label.en ?? id);
  const s = d.services;
  const services = [
    d.needsDisassembly && 'Disassembly',
    s.reassembly && 'Reassembly',
    s.placement && 'Room placement',
    s.garageOnly && `Garage-only delivery${s.garageOnlyNote ? `: ${s.garageOnlyNote}` : ''}`,
    s.unpacking && 'Unpacking',
  ].filter(Boolean);
  return {
    headline: `${sizeText(d)} · ${d.homeType ? homeTypeLabel[d.homeType] : 'Home type not chosen'}`,
    photo: propertyImages[prop].alt.en,
    items: items.length ? items.join(', ') : d.inventoryUnsure ? 'Customer not sure yet — needs review' : 'No items selected',
    special: special.length ? special.join(', ') : 'None',
    stops: d.stops.length ? d.stops.map((st, i) => `Stop ${i + 1} (${st.role}): ${[st.street, st.unit, st.zip].filter(Boolean).join(', ')}`).join(' | ') : 'None',
    services: services.length ? services.join(', ') : 'None',
    truck: d.truckPreference ? (d.truckPreference === 'help' ? 'Requested: help me choose' : `Requested: ${d.truckPreference} ft (not assigned)`) : 'No preference',
  };
}
